import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import './AuditLogPage.css';

const sortOptions = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'company', label: 'Company (A–Z)' },
  { value: 'action', label: 'Action (A–Z)' }
];

function AuditLogPage({ onLogout }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [companyFilter, setCompanyFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focusId = Number(params.get('focus'));
    if (focusId) {
      setExpandedId(focusId);
    }
  }, []);

  const loadLogs = async () => {
    setLoading(true);
    setError('');
    try {
        const data = await api.getAuditLogs({
          limit: 500,
          company: companyFilter.trim(),
          action: actionFilter.trim(),
          sort: sortBy
        });
      setLogs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companies = useMemo(() => {
    const set = new Set();
    logs.forEach((log) => {
      if (log.target_company_slug) set.add(log.target_company_slug);
      if (log.actor_company_slug) set.add(log.actor_company_slug);
    });
    return Array.from(set).sort();
  }, [logs]);

  const actions = useMemo(() => {
    const set = new Set();
    logs.forEach((log) => set.add(log.action));
    return Array.from(set).sort();
  }, [logs]);

  const filteredLogs = useMemo(() => logs, [logs]);
  const formatDate = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  const formatAction = (value) =>
    value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

  return (
    <div className="audit-page">
      <header className="audit-page-header">
        <div>
          <h1>Audit Log</h1>
          <p>Review platform-level actions across companies.</p>
        </div>
        <div className="audit-page-actions">
          <a className="secondary-link" href="/superadmin">Back to Dashboard</a>
          <button type="button" className="secondary-button" onClick={onLogout}>
            Logout
          </button>
        </div>
      </header>

      <section className="audit-controls">
        <div className="audit-input-group">
          <input
            type="text"
            list="audit-companies"
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            placeholder="Company slug (optional)"
          />
          <datalist id="audit-companies">
            {companies.map((company) => (
              <option key={company} value={company} />
            ))}
          </datalist>
        </div>
        <div className="audit-input-group">
          <input
            type="text"
            list="audit-actions"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            placeholder="Action (optional)"
          />
          <datalist id="audit-actions">
            {actions.map((action) => (
              <option key={action} value={action} />
            ))}
          </datalist>
        </div>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          {sortOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button type="button" className="secondary-button" onClick={loadLogs}>
          Refresh Logs
        </button>
        <span className="audit-count" aria-live="polite">{logs.length} records</span>
      </section>

      {loading ? (
        <div className="audit-card">Loading audit logs...</div>
      ) : error ? (
        <div className="audit-card error">{error}</div>
      ) : logs.length === 0 ? (
        <div className="audit-card">No logs found for the selected filters.</div>
      ) : (
        <div className="audit-list">
          {filteredLogs.map((log) => (
            <div key={log.id} className={`audit-item ${expandedId === log.id ? 'expanded' : ''}`}>
              <button
                type="button"
                className="audit-item-summary"
                onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
              >
                <div className="audit-main">
                  <div className="audit-title-row">
                    <span className="audit-action">{formatAction(log.action)}</span>
                    {log.target_company_slug && (
                      <span className="audit-chip">{log.target_company_slug}</span>
                    )}
                  </div>
                  <span className="audit-sub">
                    {log.actor_username || 'system'} @ {log.actor_company_slug || 'n/a'}
                  </span>
                </div>
                <div className="audit-meta">
                  <span className="audit-time">{formatDate(log.created_at)}</span>
                </div>
              </button>
              {expandedId === log.id && (
                <div className="audit-details">
                  <div>
                    <span className="label">Actor</span>
                    <span>{log.actor_username || 'system'} ({log.actor_company_slug || 'n/a'})</span>
                  </div>
                  <div>
                    <span className="label">Target Company</span>
                    <span>{log.target_company_slug || '—'}</span>
                  </div>
                  <div>
                    <span className="label">Metadata</span>
                    <pre>{log.metadata ? JSON.stringify(log.metadata, null, 2) : '—'}</pre>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AuditLogPage;
